import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { QrCode, Users as UsersIcon, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../i18n/I18nProvider';
import { submitRSVP } from '../lib/api';
import { useEvent, calendarEvent } from '../i18n/EventProvider';
import AddToCalendar from './AddToCalendar';
import InviteFriend from './InviteFriend';

const EMPTY = { name: '', email: '', phone: '', count: '1', kids: false, note: '' };

function RsvpDone({ t, ev, onAgain }) {
  return (
    <div data-testid="rsvp-success-panel" className="bg-[#f5efe3] rounded-3xl p-7 lg:p-9 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.4)] text-[#1f3a2a]">
      <span className="w-12 h-12 rounded-full bg-[#1f3a2a] text-[#d4a63d] flex items-center justify-center"><Check size={22} /></span>
      <h3 className="font-serif-display text-3xl mt-5">{t.invite.doneTitle}</h3>
      <p className="mt-3 text-[#4a5a4e] leading-relaxed">{t.invite.doneText}</p>
      <div className="mt-7 flex flex-wrap gap-3">
        <InviteFriend variant="light" testid="rsvp-success-invite-btn" />
        <AddToCalendar event={ev} variant="dark" />
      </div>
      <button type="button" onClick={onAgain} data-testid="rsvp-another-btn" className="mt-6 text-sm text-[#2e5340] underline underline-offset-4 hover:text-[#1f3a2a]">{t.invite.another}</button>
    </div>
  );
}

export default function RSVP() {
  const { t, lang } = useI18n();
  const ev = calendarEvent(useEvent().event, lang);
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast({ title: t.rsvp.requiredTitle, description: t.rsvp.requiredText });
      return;
    }
    setLoading(true);
    try {
      await submitRSVP(form);
      setForm(EMPTY);
      setDone(true);
      toast({ title: t.rsvp.okTitle, description: t.rsvp.okText });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rsvp" className="relative py-24 lg:py-32 bg-[#1f3a2a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <div className="text-[#f5efe3]">
          <span className="text-[#d4a63d] text-xs uppercase tracking-wider-xtra font-medium">{t.rsvp.eyebrow}</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">{t.rsvp.title_1}<br /><span className="italic text-[#e8c87a]">{t.rsvp.title_2}</span></h2>
          <p className="mt-6 text-[#f5efe3]/80 text-lg leading-relaxed max-w-md">{t.rsvp.sub}</p>

          <div className="mt-10 inline-flex items-start gap-4 bg-[#2e5340]/60 border border-[#f5efe3]/10 rounded-2xl p-5">
            <span className="w-14 h-14 rounded-xl bg-[#f5efe3] flex items-center justify-center text-[#1f3a2a] shrink-0"><QrCode size={28} /></span>
            <div>
              <div className="font-serif-display text-xl">{t.rsvp.qrTitle}</div>
              <p className="text-[#f5efe3]/70 text-sm mt-1 max-w-xs">{t.rsvp.qrText}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <AddToCalendar event={ev} variant="dark" />
            <InviteFriend variant="dark" testid="rsvp-invite-friend-btn" />
          </div>

          <p className="mt-10 text-[#f5efe3]/60 text-sm">{t.rsvp.walkin}</p>
        </div>

        {done ? <RsvpDone t={t} ev={ev} onAgain={() => setDone(false)} /> : (
        <form onSubmit={submit} data-testid="rsvp-form" className="bg-[#f5efe3] rounded-3xl p-7 lg:p-9 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.4)] space-y-5">
          <div>
            <Label className="text-[#1f3a2a]">{t.rsvp.name} <span className="text-[#b88a2a]">*</span></Label>
            <Input data-testid="rsvp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.visitor.yourName} className="mt-2 bg-white border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1f3a2a]">{t.rsvp.email}</Label>
              <Input data-testid="rsvp-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.visitor.emailPh} className="mt-2 bg-white border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
            </div>
            <div>
              <Label className="text-[#1f3a2a]">{t.rsvp.phone}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.rsvp.optional} className="mt-2 bg-white border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
            </div>
          </div>
          <div>
            <Label className="text-[#1f3a2a] inline-flex items-center gap-2"><UsersIcon size={14} /> {t.rsvp.count}</Label>
            <Input type="number" min="1" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className="mt-2 bg-white border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
          </div>
          <label className="flex items-center gap-3 text-sm text-[#1f3a2a] cursor-pointer">
            <Checkbox checked={form.kids} onCheckedChange={(v) => setForm({ ...form, kids: !!v })} className="border-[#1f3a2a]/40 data-[state=checked]:bg-[#2e5340] data-[state=checked]:border-[#2e5340]" />
            {t.rsvp.kids}
          </label>
          <div>
            <Label className="text-[#1f3a2a]">{t.rsvp.note}</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t.rsvp.notePh} className="mt-2 bg-white border-[#e5dcc5] rounded-xl focus-visible:ring-[#2e5340] min-h-[100px]" />
          </div>
          <Button type="submit" disabled={loading} data-testid="rsvp-submit-btn" className="w-full bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3] rounded-full h-12 font-medium">
            {loading ? t.rsvp.sending : t.rsvp.submit}
          </Button>
        </form>
        )}
      </div>
    </section>
  );
}
